import { useQuery, useMutation, useQueryClient, type UseQueryResult, type UseMutationResult } from '@tanstack/react-query';
import { client } from './client';
export type {
  ProposalStatus,
  ShadowDecision,
  SkipReason,
  MatchCategory,
} from '../../../shared/proposal-contract.js';
import type { ProposalStatus, ShadowDecision, SkipReason, MatchCategory } from '../../../shared/proposal-contract.js';
// Контракт trace-viewer — из types/, единым объявлением: локальная копия
// здесь была урезанной (без mode, strategy, turnKind).
import type { GenerationContext, ObserverSignals } from '../types';
export type { GenerationContext, ObserverSignals };

// ============================================================================
// Types
// ============================================================================

export type ActorRole = 'client' | 'operator';
export type TurnStatus = 'open' | 'closed';
export type CloseReason = 'silence_timeout' | 'actor_switch' | 'manual_flush';

// Phase 3: Proposals

export interface TelegramChatSummary {
  chatId: number;
  lastTurnAt: string | null;
  preview: string | null;
  turnCount: number;
}

export interface TelegramTurn {
  id: string;
  chatId: number;
  actorId: number;
  actorUsername: string | null;
  actorRole: ActorRole | null;
  text: string;
  messageCount: number;
  startedAt: string;
  closedAt: string | null;
  closeReason: CloseReason | null;
  status: TurnStatus;
}

export interface TelegramChatParticipant {
  actorId: number;
  actorUsername: string | null;
  actorRole: ActorRole | null;
  turnCount: number;
  messageCount: number;
}

export interface TelegramChatDetails {
  chatId: number;
  turnCount: number;
  messageCount: number;
  firstTurnAt: string | null;
  lastTurnAt: string | null;
  participants: TelegramChatParticipant[];
}

export type MessageSourceType = 'telegram' | 'telegram_outbound';

export interface TelegramMessage {
  id: string;
  chatId: number;
  messageId: number;
  fromUserId: number | null;
  fromUsername: string | null;
  text: string | null;
  sourceType: MessageSourceType;
  receivedAt: string;
  isOutbound: boolean;
}

// Phase 3: Proposals
export interface TelegramProposal {
  id: string;
  chatId: number;
  sourceTurnId: string;
  responseTurnId: string | null;
  text: string;
  /** Что предложил движок до правки оператора. `null` = не правили. */
  originalText: string | null;
  status: ProposalStatus;
  observerSignals: ObserverSignals | null;
  shadowDecision: ShadowDecision | null;
  skipReason: SkipReason | null;
  executorApplied: boolean;
  matchScore: number | null;
  matchCategory: MatchCategory | null;
  generationContext: GenerationContext | null; // для trace viewer
  createdAt: string;
  matchedAt: string | null;
}

// ============================================================================
// API Responses
// ============================================================================

interface ChatsResponse {
  chats: TelegramChatSummary[];
}

interface TurnsResponse {
  turns: TelegramTurn[];
  nextCursor: string | null;
}

interface MessagesResponse {
  messages: TelegramMessage[];
}

interface SendMessageResponse {
  ok: boolean;
  messageId?: number;
  error?: string;
}

// Phase 3: Proposals
interface ProposalsResponse {
  proposals: TelegramProposal[];
}


// ============================================================================
// Hooks
// ============================================================================

/**
 * Fetches list of Telegram chats with summaries.
 */
export function useTelegramChats(): UseQueryResult<TelegramChatSummary[], Error> {
  return useQuery({
    queryKey: ['telegram', 'chats'],
    queryFn: async () => {
      const response = await client.get<ChatsResponse>('/telegram/chats');
      return response.data.chats;
    },
    refetchInterval: 5000, // Refresh every 5 seconds
  });
}

/**
 * Fetches messages for a specific chat (for chat UI).
 */
export function useTelegramMessages(
  chatId: number | null,
  options: { limit?: number } = {}
): UseQueryResult<TelegramMessage[], Error> {
  const { limit = 100 } = options;

  return useQuery({
    queryKey: ['telegram', 'messages', chatId, limit],
    queryFn: async () => {
      const response = await client.get<MessagesResponse>(
        `/telegram/chats/${chatId}/messages`,
        { params: { limit } }
      );
      return response.data.messages;
    },
    enabled: chatId !== null,
    refetchInterval: 1000, // Refresh every 1 second for live feeling
  });
}

/**
 * Fetches turns for a specific chat (for debug/analysis).
 */
export function useTelegramTurns(
  chatId: number | null,
  options: { status?: 'open' | 'closed' | 'all'; limit?: number } = {}
): UseQueryResult<TelegramTurn[], Error> {
  const { status = 'all', limit = 100 } = options;

  return useQuery({
    queryKey: ['telegram', 'turns', chatId, status, limit],
    queryFn: async () => {
      const response = await client.get<TurnsResponse>(
        `/telegram/chats/${chatId}/turns`,
        { params: { status, limit } }
      );
      return response.data.turns;
    },
    enabled: chatId !== null,
    refetchInterval: 1000, // Refresh every 1 second for live feeling
  });
}

/**
 * Fetches details for a specific chat.
 */
export function useTelegramChatDetails(
  chatId: number | null
): UseQueryResult<TelegramChatDetails, Error> {
  return useQuery({
    queryKey: ['telegram', 'chat', chatId],
    queryFn: async () => {
      const response = await client.get<TelegramChatDetails>(
        `/telegram/chats/${chatId}`
      );
      return response.data;
    },
    enabled: chatId !== null,
    refetchInterval: 5000,
  });
}

/**
 * Sends a message to a Telegram chat.
 */
export function useSendTelegramMessage(chatId: number | null): UseMutationResult<SendMessageResponse, Error, string> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (text: string) => {
      if (!chatId) {
        throw new Error('No chat selected');
      }
      const response = await client.post<SendMessageResponse>(
        `/telegram/chats/${chatId}/send`,
        { text }
      );
      return response.data;
    },
    onSuccess: () => {
      // Invalidate messages, turns, and proposals to refetch after sending
      void queryClient.invalidateQueries({ queryKey: ['telegram', 'messages', chatId] });
      void queryClient.invalidateQueries({ queryKey: ['telegram', 'turns', chatId] });
      // After sending, proposals may get matched
      void queryClient.invalidateQueries({ queryKey: ['telegram', 'proposals', chatId] });
    },
  });
}

// ============================================================================
// Phase 3: Proposals Hooks
// ============================================================================

/**
 * Fetches proposals for a specific chat.
 */
export function useTelegramProposals(
  chatId: number | null,
  options: { status?: ProposalStatus | 'all'; limit?: number } = {}
): UseQueryResult<TelegramProposal[], Error> {
  const { status = 'all', limit = 50 } = options;

  return useQuery({
    queryKey: ['telegram', 'proposals', chatId, status, limit],
    queryFn: async () => {
      const response = await client.get<ProposalsResponse>(
        `/telegram/chats/${chatId}/proposals`,
        { params: { status, limit } }
      );
      return response.data.proposals;
    },
    enabled: chatId !== null,
    refetchInterval: 2000, // Refresh every 2 seconds
  });
}

/*
 * Ручная генерация черновика переехала в `useGenerateProposal` в `api/inbox.ts`
 * (09.09): она нужна всем трём каналам, а здесь была телеграмной, зовущей
 * маршрут, которого больше нет. Интерфейс её при этом не звал ни разу —
 * хук был написан и мёртв.
 */
